import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import {
  Form,
  Input,
  Button,
  Card,
  Table,
  Space,
  InputNumber,
  Select,
  Divider,
  Row,
  Col,
  Typography,
  Tag,
  Alert,
  Tooltip,
  Badge,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  StarOutlined,
  StarFilled,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import axios from "axios";
import PageTitle from "../page-header/PageHeader";

const { Title, Text } = Typography;

const ItemGroupForm = () => {
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // All running sizes available
  const [runningSizes, setRunningSizes] = useState([]);
  const [loadingRS, setLoadingRS] = useState(false);

  // Members currently in the group
  // Each member: { running_size_id, name, qty_per_group, is_parent, _rs (full RS object) }
  const [members, setMembers] = useState([]);
  const [selectedRSId, setSelectedRSId] = useState(null);

  // Fetch running sizes
  useEffect(() => {
    const fetchRS = async () => {
      setLoadingRS(true);
      try {
        const { data } = await axios.get("running-size?status=true&page=1&count=500");
        setRunningSizes(data?.data || data?.getAllRunningSize || data || []);
      } catch {
        toast.error("Failed to load running sizes");
      } finally {
        setLoadingRS(false);
      }
    };
    fetchRS();
  }, []);

  // Fetch existing group if editing
  useEffect(() => {
    if (!isEditing) return;
    const fetchGroup = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`item-group/${id}`);
        form.setFieldsValue({ name: data.name, remarks: data.remarks || "" });
        const rawMembers = Array.isArray(data.members) ? data.members : [];
        setMembers(
          rawMembers.map((m) => ({
            ...m,
            _rs: m.running_size || null,
          }))
        );
      } catch {
        toast.error("Failed to load group");
      } finally {
        setLoading(false);
      }
    };
    fetchGroup();
  }, [id, isEditing]);

  // Add a running size to the group
  const handleAddMember = () => {
    if (!selectedRSId) return;
    if (members.some((m) => m.running_size_id === selectedRSId)) {
      toast.warning("This running size is already in the group");
      return;
    }
    const rs = runningSizes.find((r) => r.id === selectedRSId);
    if (!rs) return;
    setMembers((prev) => [
      ...prev,
      {
        running_size_id: rs.id,
        name: rs.name,
        qty_per_group: 1,
        is_parent: prev.length === 0, // First added is default parent
        _rs: rs,
      },
    ]);
    setSelectedRSId(null);
  };

  // Remove a member
  const handleRemove = (rsId) => {
    setMembers((prev) => {
      const filtered = prev.filter((m) => m.running_size_id !== rsId);
      // If we removed the parent, promote the first remaining member
      const hadParent = prev.find((m) => m.running_size_id === rsId)?.is_parent;
      if (hadParent && filtered.length > 0 && !filtered.some((m) => m.is_parent)) {
        filtered[0] = { ...filtered[0], is_parent: true };
      }
      return filtered;
    });
  };

  // Change quantity per group
  const handleQtyChange = (rsId, qty) => {
    setMembers((prev) =>
      prev.map((m) => (m.running_size_id === rsId ? { ...m, qty_per_group: qty } : m))
    );
  };

  // Set which member is the parent
  const handleSetParent = (rsId) => {
    setMembers((prev) =>
      prev.map((m) => ({ ...m, is_parent: m.running_size_id === rsId }))
    );
  };

  // Pricing summary
  const parentMember = members.find((m) => m.is_parent);
  const groupTotal = members.reduce((sum, m) => {
    const rate = m._rs?.last_sale_price || 0;
    return sum + rate * (m.qty_per_group || 1);
  }, 0);
  const parentWisePrice =
    parentMember && parentMember.qty_per_group
      ? groupTotal / parentMember.qty_per_group
      : 0;

  const handleSave = async () => {
    if (members.length < 2) {
      toast.error("A group must have at least 2 members");
      return;
    }
    if (!members.some((m) => m.is_parent)) {
      toast.error("Please select a parent item");
      return;
    }
    try {
      await form.validateFields();
    } catch {
      return;
    }

    const values = form.getFieldsValue();
    const parentRS = members.find((m) => m.is_parent);
    const customer_id = parentRS?._rs?.customer_id || "";

    const payload = {
      name: values.name,
      customer_id,
      parent_running_size_id: parentRS.running_size_id,
      members: members.map((m) => ({
        running_size_id: m.running_size_id,
        name: m.name,
        qty_per_group: m.qty_per_group,
        is_parent: m.is_parent,
      })),
      remarks: values.remarks || null,
    };

    setSaving(true);
    try {
      if (isEditing) {
        await axios.put(`item-group/${id}`, payload);
        toast.success("Item group updated");
      } else {
        await axios.post("item-group", payload);
        toast.success("Item group created");
      }
      navigate("/item-group");
    } catch (err) {
      toast.error(err?.response?.data || "Failed to save group");
    } finally {
      setSaving(false);
    }
  };

  // Already-added RS ids (to exclude from selector)
  const addedIds = members.map((m) => m.running_size_id);

  const memberColumns = [
    {
      title: "Parent",
      key: "parent",
      width: 70,
      align: "center",
      render: (_, record) =>
        record.is_parent ? (
          <Tooltip title="Parent item — group price is expressed per this item">
            <StarFilled style={{ color: "#faad14", fontSize: 18 }} />
          </Tooltip>
        ) : (
          <Tooltip title="Set as parent">
            <StarOutlined
              style={{ color: "#ccc", fontSize: 18, cursor: "pointer" }}
              onClick={() => handleSetParent(record.running_size_id)}
            />
          </Tooltip>
        ),
    },
    {
      title: "Running Size",
      key: "name",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.name}</Text>
          {record._rs && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record._rs.length}×{record._rs.width}×{record._rs.height} {record._rs.unit_type} | {record._rs.ply} Ply
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Unit Rate (₹)",
      key: "rate",
      width: 130,
      render: (_, record) => {
        const rate = record._rs?.last_sale_price;
        return rate != null ? (
          <Text>₹{Number(rate).toFixed(2)}</Text>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: (
        <span>
          Qty / Group{" "}
          <Tooltip title="How many of this item are in one complete group set">
            <InfoCircleOutlined style={{ color: "#999" }} />
          </Tooltip>
        </span>
      ),
      key: "qty",
      width: 140,
      render: (_, record) => (
        <InputNumber
          min={1}
          value={record.qty_per_group}
          onChange={(v) => handleQtyChange(record.running_size_id, v || 1)}
          style={{ width: 100 }}
        />
      ),
    },
    {
      title: "Sub-total",
      key: "subtotal",
      width: 120,
      render: (_, record) => {
        const rate = record._rs?.last_sale_price || 0;
        const sub = rate * (record.qty_per_group || 1);
        return (
          <Text>
            {record.qty_per_group}× ₹{Number(rate).toFixed(2)} = <strong>₹{sub.toFixed(2)}</strong>
          </Text>
        );
      },
    },
    {
      title: "",
      key: "remove",
      width: 60,
      render: (_, record) => (
        <Button
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemove(record.running_size_id)}
        />
      ),
    },
  ];

  if (!isLogged) return <Navigate to="/auth/login" replace />;

  return (
    <>
      <PageTitle title="Back" />
      <Card
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>
              {isEditing ? "Edit Item Group" : "Create Item Group"}
            </Title>
          </Space>
        }
        loading={loading}
      >
        <Form form={form} layout="vertical" style={{ maxWidth: 800 }}>
          <Row gutter={16}>
            <Col xs={24} md={16}>
              <Form.Item
                name="name"
                label="Group Name"
                rules={[{ required: true, message: "Please enter a group name" }]}
              >
                <Input placeholder="e.g. Juice Box Set — 200ml" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="remarks" label="Remarks">
                <Input placeholder="Optional notes" />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Divider orientation="left">Group Members</Divider>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <span>
              Add Running Sizes below and set quantities per group. Click the{" "}
              <StarOutlined /> star to mark the <strong>parent item</strong> — the group price
              will be expressed per 1 parent unit.
            </span>
          }
        />

        {/* Member selector */}
        <Space style={{ marginBottom: 16 }}>
          <Select
            showSearch
            placeholder="Search & select a running size to add…"
            style={{ width: 500 }}
            loading={loadingRS}
            value={selectedRSId}
            onChange={setSelectedRSId}
            optionFilterProp="children"
            filterOption={(input, option) =>
              String(option?.children ?? option?.label ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            allowClear
          >
            {runningSizes
              .filter((rs) => !addedIds.includes(rs.id))
              .map((rs) => (
                <Select.Option key={rs.id} value={rs.id}>
                  {rs.name} — {rs.length}×{rs.width}×{rs.height} {rs.unit_type} | {rs.ply} Ply
                  {rs.customer?.name ? ` | ${rs.customer.name}` : ""}
                </Select.Option>
              ))}
          </Select>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddMember}
            disabled={!selectedRSId}
          >
            Add to Group
          </Button>
        </Space>

        <Table
          dataSource={members}
          columns={memberColumns}
          rowKey="running_size_id"
          pagination={false}
          size="middle"
          bordered
          locale={{ emptyText: "No members yet — add running sizes above" }}
          rowClassName={(record) => (record.is_parent ? "group-parent-row" : "")}
        />

        {/* Pricing Summary */}
        {members.length >= 2 && (
          <>
            <Divider orientation="left">Pricing Summary</Divider>
            <Card
              size="small"
              style={{ background: "#f6ffed", borderColor: "#b7eb8f", maxWidth: 600 }}
            >
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                {members.map((m, i) => {
                  const rate = m._rs?.last_sale_price || 0;
                  return (
                    <Row key={i} justify="space-between">
                      <Col>
                        {m.is_parent && <Tag color="gold">parent</Tag>}{" "}
                        {m.qty_per_group}× {m.name}
                        <Text type="secondary"> @ ₹{rate.toFixed(2)}</Text>
                      </Col>
                      <Col>
                        <Text strong>₹{(rate * m.qty_per_group).toFixed(2)}</Text>
                      </Col>
                    </Row>
                  );
                })}
                <Divider style={{ margin: "8px 0" }} />
                <Row justify="space-between">
                  <Col><Text strong>Group Total (1 set)</Text></Col>
                  <Col><Text strong>₹{groupTotal.toFixed(2)}</Text></Col>
                </Row>
                {parentMember && (
                  <Row justify="space-between" style={{ background: "#fff", padding: "4px 8px", borderRadius: 4 }}>
                    <Col>
                      <Text strong style={{ color: "#1677ff" }}>
                        {parentMember.name}-wise price
                        {parentMember.qty_per_group > 1 && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {" "}(÷ {parentMember.qty_per_group})
                          </Text>
                        )}
                      </Text>
                    </Col>
                    <Col>
                      <Text strong style={{ fontSize: 18, color: "#1677ff" }}>
                        ₹{parentWisePrice.toFixed(2)}
                      </Text>
                    </Col>
                  </Row>
                )}
                <Text type="secondary" style={{ fontSize: 11 }}>
                  * Pricing uses last sale price of each running size. Live rates calculated at quotation time.
                </Text>
              </Space>
            </Card>

            {/* Per-member wise breakdown */}
            <Card size="small" style={{ maxWidth: 600, marginTop: 8 }} title="All member-wise prices">
              {members.map((m, i) => {
                const memberWise = m.qty_per_group ? groupTotal / m.qty_per_group : 0;
                return (
                  <Row key={i} justify="space-between" style={{ padding: "2px 0" }}>
                    <Col>
                      {m.is_parent && <Badge color="gold" />}{" "}
                      Per {m.name.slice(0, 25)}{m.name.length > 25 ? "…" : ""}
                      <Text type="secondary" style={{ fontSize: 11 }}> (qty/group: {m.qty_per_group})</Text>
                    </Col>
                    <Col>
                      <Text>₹{memberWise.toFixed(2)}</Text>
                    </Col>
                  </Row>
                );
              })}
            </Card>
          </>
        )}

        <Divider />
        <Space>
          <Button
            type="primary"
            size="large"
            onClick={handleSave}
            loading={saving}
            disabled={members.length < 2}
          >
            {isEditing ? "Update Group" : "Save Group"}
          </Button>
          <Button size="large" onClick={() => navigate("/item-group")}>
            Cancel
          </Button>
        </Space>
      </Card>

      <style>{`
        .group-parent-row {
          background: #fffbe6 !important;
        }
      `}</style>
    </>
  );
};

export default ItemGroupForm;
